/**
 * FogBugz Formulator schemas (epic PI-SPEC-SW-E309, ER-1665 Package C,
 * contract fogbugz-formulator 1).
 *
 * The Toolbox FogBugz Formulator interviews on the person's own Claude Code
 * seat with the product code open. SpecTree keeps the session (database,
 * sliding two-hour idle lifetime), checks coverage and the Bug bar, and files
 * the case with the person's own FogBugz key; it makes no AI call for this
 * flow. One session travels like this:
 *
 * 1. The page shows the routing screen (FogBugz New Case order) and starts
 *    the session on SpecTree ({@link fogbugzFormulatorStartBodySchema});
 *    SpecTree resolves the FogBugz ↔ ADO project link and returns the
 *    session ({@link fogbugzFormulatorSessionViewSchema}).
 * 2. The page hands the session to the Toolbox
 *    (`spectreeToolbox.fogbugzFormulator.start`). For each turn the engine
 *    writes `turn.json` ({@link fogbugzFormulatorTurnInputSchema}) and runs
 *    the internal skill, which prints one line:
 *    `SPECTREE_FOGBUGZ_FORMULATOR_TURN {json}`
 *    ({@link fogbugzFormulatorTurnMarkerSchema}).
 * 3. The engine posts the turn ({@link fogbugzFormulatorTurnBodySchema}).
 * 4. When coverage is complete the page reads the review
 *    ({@link fogbugzFormulatorReviewSchema}: `intent.md`, the Bug bar result
 *    {@link fogbugzBugBarResultSchema}, possible duplicates).
 * 5. Submit is one multipart request: the JSON part
 *    ({@link fogbugzFormulatorSubmitMetadataSchema}) plus the screenshots
 *    and `.evtx` files read back from the Toolbox; SpecTree refuses a bug
 *    case below the bar (400 `BELOW_BUG_BAR`) and otherwise returns the new
 *    case ({@link fogbugzFormulatorSubmitResultSchema}).
 *
 * Request schemas are strict (unknown keys are refused). Response schemas
 * are lenient, so a SpecTree release may add response fields without a
 * contract bump. Marker JSON is untrusted model output: parsed with these
 * schemas and capped.
 */
import { z } from "zod";
import { absoluteFilePathSchema } from "../formulator/api.js";
import { askHandoffSchema } from "../ask/handoff.js";
import { MAX_GROOMING_REPOS } from "../grooming/constants.js";
import { TOOLBOX_PERSONAS } from "../roles/types.js";
import { isoDateTime } from "../zod-helpers.js";
import { FOGBUGZ_EVTX_SECTIONS_MAX_CHARS } from "../fogbugz/constants.js";
import { fogbugzCaseIdSchema } from "../fogbugz/schemas.js";
import { fogbugzLinkRepoRefSchema } from "../fogbugz/link.js";
import { FOGBUGZ_BUG_BAR_SOURCES, FOGBUGZ_FORMULATOR_ATTACHMENT_NAME_MAX, FOGBUGZ_FORMULATOR_AWAITING, FOGBUGZ_FORMULATOR_CODE_FINDING_NOTE_MAX, FOGBUGZ_FORMULATOR_CODE_FINDING_PATH_MAX, FOGBUGZ_FORMULATOR_CODE_FINDINGS_MAX, FOGBUGZ_FORMULATOR_DUPLICATE_SNIPPET_MAX, FOGBUGZ_FORMULATOR_DUPLICATES_MAX, FOGBUGZ_FORMULATOR_INTENT_MAX_CHARS, FOGBUGZ_FORMULATOR_KINDS, FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS, FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS, FOGBUGZ_FORMULATOR_MESSAGE_ROLES, FOGBUGZ_FORMULATOR_QUESTION_MAX, FOGBUGZ_FORMULATOR_REPLY_MAX, FOGBUGZ_FORMULATOR_SLOT_IDS, FOGBUGZ_FORMULATOR_SLOT_SOURCE_MAX, FOGBUGZ_FORMULATOR_SLOT_VALUE_MAX, FOGBUGZ_FORMULATOR_TRANSCRIPT_MAX, FOGBUGZ_FORMULATOR_TURN_INPUT_SCHEMA_VERSION, FOGBUGZ_FORMULATOR_TURN_MODES, } from "./constants.js";
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An ISO 8601 timestamp as JSON carries a Date. */
const isoDateTimeSchema = isoDateTime({ offset: true });
/** A FogBugz Formulator session id. */
export const fogbugzFormulatorSessionIdSchema = z.guid("sessionId must be a UUID");
export const fogbugzFormulatorKindSchema = z.enum(FOGBUGZ_FORMULATOR_KINDS);
export const fogbugzFormulatorSlotIdSchema = z.enum(FOGBUGZ_FORMULATOR_SLOT_IDS);
export const fogbugzFormulatorAwaitingSchema = z.enum(FOGBUGZ_FORMULATOR_AWAITING);
export const fogbugzFormulatorPersonaSchema = z.enum(TOOLBOX_PERSONAS);
/** A positive FogBugz id (`ixProject`, `ixArea`, ...). */
const fogbugzIdSchema = z.number().int().positive();
/**
 * An attachment file name: no path separators, no `..`, no leading dot,
 * at most {@link FOGBUGZ_FORMULATOR_ATTACHMENT_NAME_MAX} characters.
 */
export const fogbugzFormulatorAttachmentNameSchema = z
    .string()
    .min(1)
    .max(FOGBUGZ_FORMULATOR_ATTACHMENT_NAME_MAX)
    .refine((name) => !/[/\\\0]/.test(name) && !name.includes("..") && !name.startsWith("."), {
    message: "attachment names must be plain file names",
});
// ---------------------------------------------------------------------------
// Routing and start
// ---------------------------------------------------------------------------
/**
 * The person's routing choice, in FogBugz New Case order: Project (required),
 * Area, Milestone (`ixFixFor`), Category, Assigned To, Priority, Tags,
 * Version. The same body as the website Formulator's `routingSelectionSchema`
 * (`packages/api/src/schemas/fogbugz.ts`), names included so the page can
 * echo what it showed; SpecTree re-resolves every name from the FogBugz lists
 * it fetches itself. Routing is never inferred: nothing here has a default.
 */
export const fogbugzFormulatorRoutingSchema = z
    .object({
    ixProject: fogbugzIdSchema,
    sProject: z.string().min(1).max(200),
    ixArea: fogbugzIdSchema.optional(),
    sArea: z.string().max(200).optional(),
    ixFixFor: fogbugzIdSchema.optional(),
    sFixFor: z.string().max(200).optional(),
    ixCategory: fogbugzIdSchema.optional(),
    sCategory: z.string().max(200).optional(),
    ixPersonAssignedTo: fogbugzIdSchema.optional(),
    sPersonAssignedTo: z.string().max(200).optional(),
    ixPriority: fogbugzIdSchema.optional(),
    tags: z.array(z.string().min(1).max(50)).max(10).optional(),
    version: z.string().max(100).optional(),
})
    .strict();
/** Start a session: the routing choice and what the person is filing. */
export const fogbugzFormulatorStartBodySchema = z
    .object({
    routing: fogbugzFormulatorRoutingSchema,
    kind: fogbugzFormulatorKindSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------
/**
 * A slot Claude filled: the value and, optionally, where it came from
 * (`code: src/schedule.ts`, `screenshot 1`, `answer 3`). Mirrors Intent v1's
 * `FilledSlotValue`; `state` may be left out.
 */
export const fogbugzFormulatorFilledSlotSchema = z
    .object({
    state: z.literal("filled").optional(),
    value: z.string().trim().min(1).max(FOGBUGZ_FORMULATOR_SLOT_VALUE_MAX),
    source: z.string().trim().min(1).max(FOGBUGZ_FORMULATOR_SLOT_SOURCE_MAX).optional(),
})
    .strict();
/**
 * A slot the person explicitly could not answer, with the plain question
 * someone must answer to fill it. Mirrors Intent v1's `UnknownSlotValue`:
 * "unknown" never fills a required slot, but answers an optional one.
 */
export const fogbugzFormulatorUnknownSlotSchema = z
    .object({
    state: z.literal("unknown"),
    question: z.string().trim().min(1).max(FOGBUGZ_FORMULATOR_QUESTION_MAX),
})
    .strict();
/** One slot value: filled or explicitly unknown. */
export const fogbugzFormulatorSlotValueSchema = z.union([
    fogbugzFormulatorUnknownSlotSchema,
    fogbugzFormulatorFilledSlotSchema,
]);
/**
 * Slot values keyed by Intent v1 slot id; any subset. Unknown slot ids are
 * refused, so the key count is bounded by the slot list.
 */
export const fogbugzFormulatorSlotsSchema = z.partialRecord(fogbugzFormulatorSlotIdSchema, fogbugzFormulatorSlotValueSchema);
/** Something Claude found in the code: a repository-relative path and a plain note. */
export const fogbugzFormulatorCodeFindingSchema = z
    .object({
    path: z.string().trim().min(1).max(FOGBUGZ_FORMULATOR_CODE_FINDING_PATH_MAX),
    note: z.string().trim().min(1).max(FOGBUGZ_FORMULATOR_CODE_FINDING_NOTE_MAX),
})
    .strict();
// ---------------------------------------------------------------------------
// The engine turn
// ---------------------------------------------------------------------------
/**
 * One turn the engine produced with Claude Code, posted to SpecTree.
 *
 * - `answer`: the person's answer. Absent only on the opening turn.
 * - `extracted`: slot values from the answer, the code and the attachments.
 * - `question`: the next question, or null when Claude thinks the report is
 *   complete. SpecTree's coverage check decides completion, not Claude.
 * - `codeFindings`: what Claude found in the code; replaces the stored list.
 * - `done`: Claude has nothing more to ask.
 * - `version`: the session version the engine loaded before Claude ran (the
 *   snapshot the turn was written from). A turn that finishes after another
 *   save is refused with 409 `INTERVIEW_SESSION_CONFLICT`, so a slow turn
 *   never overwrites a newer answer or correction.
 */
export const fogbugzFormulatorTurnBodySchema = z
    .object({
    answer: z
        .string()
        .min(1)
        .max(FOGBUGZ_FORMULATOR_REPLY_MAX)
        .refine((value) => value.trim().length > 0, "answer must not be blank")
        .optional(),
    extracted: fogbugzFormulatorSlotsSchema,
    question: z.string().trim().min(1).max(FOGBUGZ_FORMULATOR_QUESTION_MAX).nullable(),
    codeFindings: z.array(fogbugzFormulatorCodeFindingSchema).max(FOGBUGZ_FORMULATOR_CODE_FINDINGS_MAX),
    done: z.boolean(),
    version: z.number().int().min(0),
})
    .strict();
/**
 * The JSON after `SPECTREE_FOGBUGZ_FORMULATOR_TURN ` on the skill's result
 * line: the engine turn without the person's answer (the engine adds it from
 * `turn.json`) or the session version (the engine adds the one it loaded),
 * plus the session it answers. The engine refuses a marker for
 * another session (`NO_TURN_RETURNED`).
 */
export const fogbugzFormulatorTurnMarkerSchema = fogbugzFormulatorTurnBodySchema
    .omit({ answer: true, version: true })
    .extend({ sessionId: fogbugzFormulatorSessionIdSchema });
// ---------------------------------------------------------------------------
// turn.json (engine -> skill)
// ---------------------------------------------------------------------------
/** The routing as the person saw it, by name, so Claude can use it in plain words. */
export const fogbugzFormulatorRoutingNamesSchema = z
    .object({
    project: z.string().min(1).max(200),
    area: z.string().max(200).optional(),
    milestone: z.string().max(200).optional(),
    category: z.string().max(200).optional(),
    assignedTo: z.string().max(200).optional(),
    priority: z.string().max(200).optional(),
    tags: z.array(z.string().min(1).max(50)).max(10).optional(),
    version: z.string().max(100).optional(),
})
    .strict();
/**
 * `turn.json`: what the engine hands the internal skill for one turn. The
 * engine writes it in the session's private turn folder; person text goes
 * here, never on the command line.
 *
 * - `mode`: `opening` (no answer yet) or `reply` (`answer` required).
 * - `persona`: the person's persona on the linked ADO project.
 * - `transcript`: the last {@link FOGBUGZ_FORMULATOR_TRANSCRIPT_MAX} messages.
 * - `slots`: the slot values so far.
 * - `repoPaths`: absolute paths of the read-only repository copies.
 * - `images` / `eventLogDigests`: file names in the turn folder (screenshots,
 *   and the deterministic digest markdown of each `.evtx`), which Claude
 *   `Read`s.
 * - `priorFindings` (optional, opening turn only; feature PI-SPEC-SW-1631):
 *   the Product Q&A hand-off the person started from (`askHandoffSchema`:
 *   their question, the plain answer, its verdict and the code findings).
 *   Untrusted hints to confirm with the person, never filled slots: the
 *   skill extracts nothing from it and SpecTree's coverage still needs every
 *   required slot answered by the person. Its `productHint` never chooses
 *   routing (the routing above is the person's).
 */
export const fogbugzFormulatorTurnInputSchema = z
    .object({
    schemaVersion: z.literal(FOGBUGZ_FORMULATOR_TURN_INPUT_SCHEMA_VERSION),
    sessionId: fogbugzFormulatorSessionIdSchema,
    mode: z.enum(FOGBUGZ_FORMULATOR_TURN_MODES),
    kind: fogbugzFormulatorKindSchema,
    persona: fogbugzFormulatorPersonaSchema,
    routing: fogbugzFormulatorRoutingNamesSchema,
    adoProject: z.string().trim().min(1).max(256),
    transcript: z
        .array(z.object({ role: z.enum(FOGBUGZ_FORMULATOR_MESSAGE_ROLES), content: z.string() }).strict())
        .max(FOGBUGZ_FORMULATOR_TRANSCRIPT_MAX),
    slots: fogbugzFormulatorSlotsSchema,
    repoPaths: z.array(absoluteFilePathSchema).min(1).max(MAX_GROOMING_REPOS),
    images: z.array(fogbugzFormulatorAttachmentNameSchema).max(FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS),
    eventLogDigests: z.array(fogbugzFormulatorAttachmentNameSchema).max(FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS),
    answer: z.string().min(1).max(FOGBUGZ_FORMULATOR_REPLY_MAX).optional(),
    priorFindings: askHandoffSchema.optional(),
})
    .strict()
    .superRefine((input, ctx) => {
    if (input.mode === "reply" && input.priorFindings !== undefined) {
        ctx.addIssue({ code: "custom", path: ["priorFindings"], message: "prior findings go on the opening turn only" });
    }
    if (input.mode === "reply" && input.answer === undefined) {
        ctx.addIssue({ code: "custom", path: ["answer"], message: "a reply turn needs the answer" });
    }
    if (input.mode === "opening" && input.answer !== undefined) {
        ctx.addIssue({ code: "custom", path: ["answer"], message: "the opening turn has no answer" });
    }
});
// ---------------------------------------------------------------------------
// Session view
// ---------------------------------------------------------------------------
/** Intent v1 coverage as the page shows it: which slots are still owed. */
export const fogbugzFormulatorCoverageSchema = z.object({
    requiredRemaining: z.array(fogbugzFormulatorSlotIdSchema),
    optionalRemaining: z.array(fogbugzFormulatorSlotIdSchema),
    isComplete: z.boolean(),
});
/**
 * A session as SpecTree returns it. `adoProject` and `repos` come from the
 * FogBugz ↔ ADO project link resolved at start; `persona` is the person's
 * persona on that ADO project. `version` is the optimistic counter a turn
 * must match (409 `INTERVIEW_SESSION_CONFLICT` otherwise); `expiresAt`
 * slides forward with every turn. Other fields pass through.
 */
export const fogbugzFormulatorSessionViewSchema = z
    .object({
    id: z.string().min(1),
    routing: fogbugzFormulatorRoutingSchema,
    kind: fogbugzFormulatorKindSchema,
    adoProject: z.string().min(1),
    repos: z.array(fogbugzLinkRepoRefSchema),
    persona: fogbugzFormulatorPersonaSchema,
    awaiting: fogbugzFormulatorAwaitingSchema,
    question: z.string().nullable(),
    coverage: fogbugzFormulatorCoverageSchema,
    version: z.number().int().min(0),
    expiresAt: isoDateTimeSchema,
})
    .loose();
// ---------------------------------------------------------------------------
// Bug bar and review
// ---------------------------------------------------------------------------
/** One rule the case misses: its id, which half of the bar, a short label and a plain message. */
export const fogbugzBugBarMissSchema = z.object({
    ruleId: z.string().min(1),
    source: z.enum(FOGBUGZ_BUG_BAR_SOURCES),
    label: z.string().min(1),
    message: z.string().min(1),
});
/**
 * The Bug bar result, the same from both FogBugz Formulators: full Intent v1
 * coverage of the bug slots plus the ADO grooming bar for a Bug (critical
 * rules refuse; advisory counts as passing). `applies` is false for a
 * feature or inquiry, which then always `passes`. `missing` names every rule
 * not met, empty when it passes.
 */
export const fogbugzBugBarResultSchema = z
    .object({
    kind: fogbugzFormulatorKindSchema,
    applies: z.boolean(),
    passes: z.boolean(),
    coverageComplete: z.boolean(),
    missing: z.array(fogbugzBugBarMissSchema),
})
    .superRefine((result, ctx) => {
    if (!result.applies && !result.passes) {
        ctx.addIssue({ code: "custom", path: ["passes"], message: "a bar that does not apply always passes" });
    }
    if (result.passes && result.missing.length > 0) {
        ctx.addIssue({ code: "custom", path: ["missing"], message: "a passing result misses nothing" });
    }
});
/** One open case that may already describe the same thing (shown, never blocking). */
export const fogbugzFormulatorDuplicateSchema = z
    .object({
    caseId: fogbugzCaseIdSchema,
    title: z.string(),
    status: z.string(),
    url: z.string(),
    snippet: z.string().max(FOGBUGZ_FORMULATOR_DUPLICATE_SNIPPET_MAX).optional(),
})
    .loose();
/**
 * The review step: the rendered `intent.md` (editable), the Bug bar result
 * and, when the search ran, possible duplicate open cases.
 */
export const fogbugzFormulatorReviewSchema = z
    .object({
    intentMarkdown: z.string().min(1).max(FOGBUGZ_FORMULATOR_INTENT_MAX_CHARS),
    bugBar: fogbugzBugBarResultSchema,
    duplicates: z.array(fogbugzFormulatorDuplicateSchema).max(FOGBUGZ_FORMULATOR_DUPLICATES_MAX).optional(),
})
    .loose();
// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------
/** One reviewed `.evtx` summary: the event log it summarizes and the four sections the person reviewed. */
export const fogbugzFormulatorEvtxSummarySchema = z
    .object({
    fileName: fogbugzFormulatorAttachmentNameSchema,
    sections: z.string().min(1).max(FOGBUGZ_EVTX_SECTIONS_MAX_CHARS),
})
    .strict();
/**
 * The JSON part of the multipart submit. `version` must match the session;
 * `intentMarkdown` is the (possibly edited) review text, which SpecTree
 * parses with the Intent v1 parser; `evtxSummaries` are the summaries the
 * person reviewed, one per event log at most. The file parts carry the
 * screenshots and `.evtx` files.
 */
export const fogbugzFormulatorSubmitMetadataSchema = z
    .object({
    version: z.number().int().min(0),
    intentMarkdown: z.string().min(1).max(FOGBUGZ_FORMULATOR_INTENT_MAX_CHARS),
    evtxSummaries: z.array(fogbugzFormulatorEvtxSummarySchema).max(FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS),
})
    .strict()
    .superRefine((body, ctx) => {
    const seen = new Set();
    body.evtxSummaries.forEach((summary, index) => {
        if (seen.has(summary.fileName)) {
            ctx.addIssue({
                code: "custom",
                path: ["evtxSummaries", index, "fileName"],
                message: `${summary.fileName} has more than one summary`,
            });
        }
        seen.add(summary.fileName);
    });
});
/** What submit returns: the new FogBugz case. */
export const fogbugzFormulatorSubmitResultSchema = z
    .object({
    caseId: fogbugzCaseIdSchema,
    caseUrl: z.string().min(1),
})
    .loose();
//# sourceMappingURL=schemas.js.map